/**
 * WhatsApp Chat Selector Modal
 * 
 * Standalone modal for selecting which WhatsApp chats to sync.
 * Triggered from toast notification after WhatsApp connection.
 * 
 * Now supports background syncing - modal closes immediately and
 * progress is shown in GlobalSyncStatusBar.
 */

import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { WhatsAppChatSelector } from './WhatsAppChatSelector';
import { useWhatsAppSync } from '@/contexts/WhatsAppSyncContext';

interface Chat {
  jid: string;
  name: string;
  isGroup: boolean;
  lastMessageTime?: number;
}

interface WhatsAppChatSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  workspaceId: string;
}

// Use secure proxy edge function (secret is added server-side)
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Rate limits for WhatsApp safety
const MAX_MESSAGES_PER_CHAT = 50; // Reduced from 100 for safety

export function WhatsAppChatSelectorModal({
  isOpen,
  onClose,
  sessionId,
  workspaceId,
}: WhatsAppChatSelectorModalProps) {
  const [isStarting, setIsStarting] = useState(false);
  
  // Get sync context for background sync support
  const { 
    startBackgroundSync, 
    updateSyncProgress, 
    completeSyncWithSuccess, 
    completeSyncWithError 
  } = useWhatsAppSync();

  // Run sync in background (called after modal closes).
  //
  // OLD path was /sessions/:id/history which only returns Baileys'
  // in-memory cache — on a fresh pair that cache is empty, so every
  // chat came back with "No cached messages for chat" and users saw
  // "synced 0 messages from N chats" (Aboudi's report, 2026-04-17).
  //
  // NEW path calls the bridge's /sessions/:id/fetch-history endpoint
  // PER CHAT. That wraps Baileys' native fetchMessageHistory() which
  // asks WhatsApp's servers for older messages and streams them back
  // via the normal message.received webhook with is_history:true.
  // That's what the whatsapp-baileys-webhook edge function already
  // handles — writes to raw_events + whatsapp_message_log. So the
  // frontend no longer batches/posts anything itself; it just kicks
  // the bridge and the server-side pipeline ingests.
  const runBackgroundSync = useCallback(async (selectedChats: Chat[]) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const chatCount = selectedChats.length;
      updateSyncProgress(sessionId, {
        current: 0,
        total: chatCount,
        chatName: 'Requesting message history from WhatsApp...',
      });

      let requestedChats = 0;
      let totalMessageCount = 0;

      // Per-chat fetch. Baileys rate-limits history requests so we run
      // serially with a 600-1200ms jitter. 14 chats = ~15s of ticking.
      for (let i = 0; i < selectedChats.length; i++) {
        const chat = selectedChats[i];

        updateSyncProgress(sessionId, {
          current: i,
          total: chatCount,
          chatName: `Fetching ${chat.name || chat.jid}... (${i + 1}/${chatCount})`,
        });

        try {
          const response = await fetch(
            `${SUPABASE_URL}/functions/v1/whatsapp-bridge-proxy/sessions/${sessionId}/fetch-history`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`,
                'apikey': SUPABASE_ANON_KEY,
              },
              body: JSON.stringify({
                chatJid: chat.jid,
                count: MAX_MESSAGES_PER_CHAT,
              }),
            },
          );

          if (response.ok) {
            const data = await response.json();
            if (data?.success) {
              requestedChats++;
              totalMessageCount += (data.messageCount ?? 0);
            } else {
              console.warn('[whatsapp-sync] fetch-history non-success for', chat.jid, data);
            }
          } else {
            console.warn('[whatsapp-sync] fetch-history HTTP', response.status, 'for', chat.jid);
          }
        } catch (err) {
          // One chat failing shouldn't stop the others.
          console.warn('[whatsapp-sync] fetch-history threw for', chat.jid, err);
        }

        // Jitter between chats (rate-limit safety).
        if (i + 1 < selectedChats.length) {
          await new Promise(r => setTimeout(r, 600 + Math.random() * 600));
        }
      }

      // The bridge streams messages back to whatsapp-baileys-webhook as
      // they arrive from WhatsApp — we don't need to post anything to
      // whatsapp-history-sync. Messages land in raw_events / message_log
      // over the next few seconds.
      completeSyncWithSuccess(sessionId, totalMessageCount, requestedChats);

    } catch (err) {
      console.error('Failed to sync history:', err);
      toast.error('Failed to sync message history');
      completeSyncWithError(sessionId);
    }
  }, [sessionId, workspaceId, updateSyncProgress, completeSyncWithSuccess, completeSyncWithError]);

  // Handle chat selection - starts background sync and closes modal immediately
  const handleChatsSelected = useCallback(async (selectedChats: Chat[]) => {
    setIsStarting(true);
    
    // Start background sync in context
    startBackgroundSync(sessionId);
    
    // Show confirmation and close modal
    toast.success(`Syncing ${selectedChats.length} chats in background...`, {
      description: 'You can continue using N71 while sync completes'
    });
    
    // Close modal immediately - sync continues in background
    onClose();
    
    // Run sync in background (non-blocking)
    runBackgroundSync(selectedChats);
    
    setIsStarting(false);
  }, [sessionId, startBackgroundSync, onClose, runBackgroundSync]);

  const handleSkip = useCallback(() => {
    onClose();
    toast.info('You can sync WhatsApp chats anytime from Settings');
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key="chat-selector-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
        onClick={isStarting ? undefined : onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 10 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 10 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-background border border-foreground/10 rounded-2xl p-8 mx-4 shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col"
        >
          <WhatsAppChatSelector
            sessionId={sessionId}
            onConfirm={handleChatsSelected}
            onSkip={handleSkip}
            isLoading={isStarting}
          />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
